import moment from 'moment-timezone';

import type {
  LeadCampaignAudience,
  LeadCampaignDeliveryStats,
  LeadCampaignEngagementStats,
  LeadCampaignSchema,
} from '../data/types';

const DEFAULT_LEADS_ADMIN_EMAILS = [
  'info@homecookapp.com',
  'hassan@homecookapp.com',
  'tayana@homecookapp.com',
];

export type DraftLeadFilter = 'include' | 'only' | 'exclude';

export type TestLeadFilter = 'include' | 'only' | 'exclude';

export const DEFAULT_DRAFT_LEAD_FILTER: DraftLeadFilter = 'exclude';

export const DEFAULT_TEST_LEAD_FILTER: TestLeadFilter = 'exclude';

export const DRAFT_LEAD_FILTER_OPTIONS = [
  { value: 'include', label: 'Include draft contacts' },
  { value: 'only', label: 'Draft contacts only' },
  { value: 'exclude', label: 'Exclude draft contacts' },
] as const;

export const TEST_LEAD_FILTER_OPTIONS = [
  { value: 'include', label: 'Include test leads' },
  { value: 'only', label: 'Test leads only' },
  { value: 'exclude', label: 'Exclude test leads' },
] as const;

/** US states and territories for CRM location filtering (expandable by country later). */
export const US_STATE_OPTIONS = [
  { country: 'US', value: 'AL', label: 'Alabama' },
  { country: 'US', value: 'AK', label: 'Alaska' },
  { country: 'US', value: 'AS', label: 'American Samoa' },
  { country: 'US', value: 'AZ', label: 'Arizona' },
  { country: 'US', value: 'AR', label: 'Arkansas' },
  { country: 'US', value: 'CA', label: 'California' },
  { country: 'US', value: 'CO', label: 'Colorado' },
  { country: 'US', value: 'CT', label: 'Connecticut' },
  { country: 'US', value: 'DE', label: 'Delaware' },
  { country: 'US', value: 'DC', label: 'District of Columbia' },
  { country: 'US', value: 'FL', label: 'Florida' },
  { country: 'US', value: 'GA', label: 'Georgia' },
  { country: 'US', value: 'GU', label: 'Guam' },
  { country: 'US', value: 'HI', label: 'Hawaii' },
  { country: 'US', value: 'ID', label: 'Idaho' },
  { country: 'US', value: 'IL', label: 'Illinois' },
  { country: 'US', value: 'IN', label: 'Indiana' },
  { country: 'US', value: 'IA', label: 'Iowa' },
  { country: 'US', value: 'KS', label: 'Kansas' },
  { country: 'US', value: 'KY', label: 'Kentucky' },
  { country: 'US', value: 'LA', label: 'Louisiana' },
  { country: 'US', value: 'ME', label: 'Maine' },
  { country: 'US', value: 'MD', label: 'Maryland' },
  { country: 'US', value: 'MA', label: 'Massachusetts' },
  { country: 'US', value: 'MI', label: 'Michigan' },
  { country: 'US', value: 'MN', label: 'Minnesota' },
  { country: 'US', value: 'MS', label: 'Mississippi' },
  { country: 'US', value: 'MO', label: 'Missouri' },
  { country: 'US', value: 'MT', label: 'Montana' },
  { country: 'US', value: 'NE', label: 'Nebraska' },
  { country: 'US', value: 'NV', label: 'Nevada' },
  { country: 'US', value: 'NH', label: 'New Hampshire' },
  { country: 'US', value: 'NJ', label: 'New Jersey' },
  { country: 'US', value: 'NM', label: 'New Mexico' },
  { country: 'US', value: 'NY', label: 'New York' },
  { country: 'US', value: 'NC', label: 'North Carolina' },
  { country: 'US', value: 'ND', label: 'North Dakota' },
  { country: 'US', value: 'MP', label: 'Northern Mariana Islands' },
  { country: 'US', value: 'OH', label: 'Ohio' },
  { country: 'US', value: 'OK', label: 'Oklahoma' },
  { country: 'US', value: 'OR', label: 'Oregon' },
  { country: 'US', value: 'PA', label: 'Pennsylvania' },
  { country: 'US', value: 'PR', label: 'Puerto Rico' },
  { country: 'US', value: 'RI', label: 'Rhode Island' },
  { country: 'US', value: 'SC', label: 'South Carolina' },
  { country: 'US', value: 'SD', label: 'South Dakota' },
  { country: 'US', value: 'TN', label: 'Tennessee' },
  { country: 'US', value: 'TX', label: 'Texas' },
  { country: 'US', value: 'UT', label: 'Utah' },
  { country: 'US', value: 'VT', label: 'Vermont' },
  { country: 'US', value: 'VI', label: 'U.S. Virgin Islands' },
  { country: 'US', value: 'VA', label: 'Virginia' },
  { country: 'US', value: 'WA', label: 'Washington' },
  { country: 'US', value: 'WV', label: 'West Virginia' },
  { country: 'US', value: 'WI', label: 'Wisconsin' },
  { country: 'US', value: 'WY', label: 'Wyoming' },
] as const;

export type LeadFilterState = {
  searchInput: string;
  filterStage: string;
  filterStatus: string;
  filterState: string;
  draftLeadFilter: DraftLeadFilter;
  testLeadFilter: TestLeadFilter;
  activeGroupId: string | null;
  filterSignupSource?: string;
  filterResourceType?: string;
};

export const DEFAULT_LEAD_FILTERS: LeadFilterState = {
  searchInput: '',
  filterStage: '',
  filterStatus: '',
  filterState: '',
  draftLeadFilter: DEFAULT_DRAFT_LEAD_FILTER,
  testLeadFilter: DEFAULT_TEST_LEAD_FILTER,
  activeGroupId: null,
};

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200] as const;

/** Format server-paginated lead list footer copy (e.g. "Showing 1 - 10 of 198 leads"). */
export const formatLeadPaginationLabel = ({
  page,
  pageSize,
  total,
}: {
  page: number;
  pageSize: number;
  total: number;
}): string => {
  if (total <= 0) return 'Showing 0 - 0 of 0 leads';
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return `Showing ${start} - ${end} of ${total} lead${total === 1 ? '' : 's'}`;
};

/** Build newsletter send payload from current CRM list filters (matches table view, not just current page). */
export const buildLeadNewsletterSendPayload = (
  input: LeadListFilterParamsInput
): { group_id?: string; list_filter?: Record<string, string | number | boolean | undefined> } => {
  if (input.activeGroupId) {
    return { group_id: input.activeGroupId };
  }

  const listFilter = { ...buildLeadListFilterParams(input) };
  delete listFilter.page;
  delete listFilter.limit;
  delete listFilter.sort;
  delete listFilter.dir;
  delete listFilter.group_id;
  return { list_filter: listFilter };
};

/** Staff selection mode for campaign recipient targeting in the leads table. */
export type LeadRecipientSelectionMode = 'none' | 'manual' | 'all_filtered';

export type LeadRecipientSelection = {
  mode: LeadRecipientSelectionMode;
  selectedLeadIds: string[];
  excludedLeadIds: string[];
};

/** Build audience snapshot for saving on a campaign draft. */
export const buildCampaignAudienceSnapshot = (
  input: LeadListFilterParamsInput & {
    activeGroupName?: string | null;
    selection?: LeadRecipientSelection;
    resolvedCount?: number;
  }
): LeadCampaignAudience => {
  const selection = input.selection;
  const resolvedCount = input.resolvedCount;

  if (selection?.mode === 'manual' && selection.selectedLeadIds.length > 0) {
    return {
      type: 'lead_ids',
      lead_ids: selection.selectedLeadIds,
      selection_mode: 'manual',
      resolved_count: selection.selectedLeadIds.length,
    };
  }

  const payload = buildLeadNewsletterSendPayload(input);

  if (selection?.mode === 'all_filtered') {
    if (payload.group_id) {
      const audience: LeadCampaignAudience = {
        type: 'group_id',
        group_id: payload.group_id,
        group_name: input.activeGroupName ?? undefined,
        selection_mode: 'all_filtered',
        resolved_count: resolvedCount,
      };
      if (selection.excludedLeadIds.length > 0) {
        audience.exclude_lead_ids = selection.excludedLeadIds;
      }
      return audience;
    }
    const listFilter = payload.list_filter ?? {};
    if (selection.excludedLeadIds.length > 0) {
      return {
        type: 'list_filter_selection',
        list_filter: listFilter,
        exclude_lead_ids: selection.excludedLeadIds,
        selection_mode: 'all_filtered',
        resolved_count: resolvedCount,
      };
    }
    return {
      type: 'list_filter',
      list_filter: listFilter,
      selection_mode: 'all_filtered',
      resolved_count: resolvedCount,
    };
  }

  if (payload.group_id) {
    return {
      type: 'group_id',
      group_id: payload.group_id,
      group_name: input.activeGroupName ?? undefined,
    };
  }
  return {
    type: 'list_filter',
    list_filter: payload.list_filter ?? {},
  };
};

/** Count selected recipients for the selection banner and send modal. */
export const countSelectedRecipients = (input: {
  selection: LeadRecipientSelection;
  totalFiltered: number;
}): number => {
  const { selection, totalFiltered } = input;
  if (selection.mode === 'manual') return selection.selectedLeadIds.length;
  if (selection.mode === 'all_filtered') {
    return Math.max(0, totalFiltered - selection.excludedLeadIds.length);
  }
  return totalFiltered;
};

/** Human-readable label for the active recipient selection mode. */
export const describeRecipientSelection = (input: {
  selection: LeadRecipientSelection;
  totalFiltered: number;
  activeGroupName?: string | null;
}): string => {
  const count = countSelectedRecipients(input);
  if (input.selection.mode === 'manual' && count > 0) {
    return `${count} lead${count === 1 ? '' : 's'} manually selected`;
  }
  if (input.selection.mode === 'all_filtered') {
    const scope = input.activeGroupName
      ? `saved group "${input.activeGroupName}"`
      : 'current filtered view';
    if (input.selection.excludedLeadIds.length > 0) {
      return `All ${count} lead${count === 1 ? '' : 's'} in ${scope} (${input.selection.excludedLeadIds.length} excluded)`;
    }
    return `All ${count} lead${count === 1 ? '' : 's'} in ${scope}`;
  }
  return '';
};

export const LEAD_CAMPAIGN_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sending', label: 'Sending' },
  { value: 'completed', label: 'Completed' },
  { value: 'partial_failure', label: 'Partial failure' },
  { value: 'failed', label: 'Failed' },
] as const;

/** Human-readable label for a campaign status value. */
export const labelForCampaignStatus = (value?: string): string =>
  LEAD_CAMPAIGN_STATUS_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '-';

/** Tailwind-friendly color tokens for campaign status badges. */
export const campaignStatusColor = (
  value?: string
): { primary: string; background: string } => {
  switch (value) {
    case 'draft':
      return { primary: '#737373', background: '#F5F5F5' };
    case 'sending':
      return { primary: '#2563EB', background: '#EFF6FF' };
    case 'completed':
      return { primary: '#0F973D', background: '#E8F5EC' };
    case 'partial_failure':
      return { primary: '#B45309', background: '#FFF4E5' };
    case 'failed':
      return { primary: '#DC2626', background: '#FEF2F2' };
    default:
      return { primary: '#737373', background: '#F5F5F5' };
  }
};

/** Summarize engagement counters for campaign tracking UI. */
export const formatCampaignEngagementSummary = (stats?: LeadCampaignEngagementStats): string => {
  if (!stats) return 'No engagement data yet.';
  const parts = [
    `${stats.unique_opens ?? 0} unique opens`,
    `${stats.opened ?? 0} total opens`,
    `${stats.unique_clicks ?? 0} unique clicks`,
    `${stats.clicked ?? 0} total clicks`,
    `${stats.replied ?? 0} replied`,
    `${stats.bounced ?? 0} bounced`,
    `${stats.spam_reports ?? 0} spam reports`,
    `${stats.unsubscribed ?? 0} unsubscribed`,
  ];
  return parts.join(' · ');
};

/** Summarize delivery counters for campaign tracking UI. */
export const formatCampaignDeliverySummary = (
  stats?: LeadCampaignDeliveryStats,
  engagement?: LeadCampaignEngagementStats
): string => {
  if (!stats) return 'No delivery data yet.';
  const parts = [
    `${stats.requested ?? 0} requested`,
    `${stats.sent ?? 0} sent`,
    `${stats.failed ?? 0} failed`,
    `${stats.skipped ?? 0} skipped`,
    `${stats.simulated ?? 0} simulated`,
    `${engagement?.delivered ?? 0} delivered`,
  ];
  return parts.join(' · ');
};

export type CampaignStatItem = {
  label: string;
  value: number | string;
};

/** Structured delivery + engagement stats for campaign detail panels. */
export const buildCampaignDeliveryStatItems = (
  delivery?: LeadCampaignDeliveryStats,
  engagement?: LeadCampaignEngagementStats
): { delivery: CampaignStatItem[]; engagement: CampaignStatItem[] } => ({
  delivery: [
    { label: 'Requested', value: delivery?.requested ?? 0 },
    { label: 'Sent', value: delivery?.sent ?? 0 },
    { label: 'Failed', value: delivery?.failed ?? 0 },
    { label: 'Skipped', value: delivery?.skipped ?? 0 },
    { label: 'Simulated', value: delivery?.simulated ?? 0 },
    { label: 'Delivered', value: engagement?.delivered ?? 0 },
  ],
  engagement: [
    { label: 'Unique opens', value: engagement?.unique_opens ?? 0 },
    { label: 'Total opens', value: engagement?.opened ?? 0 },
    { label: 'Unique clicks', value: engagement?.unique_clicks ?? 0 },
    { label: 'Total clicks', value: engagement?.clicked ?? 0 },
    { label: 'Replied', value: engagement?.replied ?? 0 },
    { label: 'Bounced', value: engagement?.bounced ?? 0 },
    { label: 'Spam reports', value: engagement?.spam_reports ?? 0 },
    { label: 'Unsubscribed', value: engagement?.unsubscribed ?? 0 },
  ],
});

/** Open rate as a percentage string (unique opens / sent). */
export const formatCampaignOpenRate = (
  delivery?: LeadCampaignDeliveryStats,
  engagement?: LeadCampaignEngagementStats
): string => {
  const sent = delivery?.sent ?? 0;
  if (sent <= 0) return '—';
  const rate = Math.round(((engagement?.unique_opens ?? 0) / sent) * 100);
  return `${rate}%`;
};

/** Click rate as a percentage string (unique clicks / sent). */
export const formatCampaignClickRate = (
  delivery?: LeadCampaignDeliveryStats,
  engagement?: LeadCampaignEngagementStats
): string => {
  const sent = delivery?.sent ?? 0;
  if (sent <= 0) return '—';
  const rate = Math.round(((engagement?.unique_clicks ?? 0) / sent) * 100);
  return `${rate}%`;
};

export type CampaignKpiItem = {
  key: string;
  label: string;
  value: string;
};

/** Compact collapsed chips and expanded hero KPIs for campaign cards. */
export const buildCampaignPrimaryKpis = (
  delivery?: LeadCampaignDeliveryStats,
  engagement?: LeadCampaignEngagementStats
): { collapsed: CampaignKpiItem[]; hero: CampaignKpiItem[] } => {
  const sent = delivery?.sent ?? 0;
  const delivered = engagement?.delivered ?? 0;
  const uniqueClicks = engagement?.unique_clicks ?? 0;
  const replies = engagement?.replied ?? 0;
  const openRate = formatCampaignOpenRate(delivery, engagement);
  const clickRate = formatCampaignClickRate(delivery, engagement);

  return {
    collapsed: [
      { key: 'delivered', label: 'Delivered', value: `${delivered}/${sent}` },
      { key: 'open_rate', label: 'Open rate', value: openRate },
      { key: 'clicks', label: 'Clicks', value: String(uniqueClicks) },
      { key: 'replies', label: 'Replies', value: String(replies) },
    ],
    hero: [
      { key: 'delivered', label: 'Delivered', value: String(delivered) },
      { key: 'open_rate', label: 'Open rate', value: openRate },
      { key: 'click_rate', label: 'Click rate', value: clickRate },
      { key: 'replies', label: 'Replies', value: String(replies) },
    ],
  };
};

/** Non-zero problem metrics for the expanded campaign issues row. */
export const buildCampaignIssueStats = (
  delivery?: LeadCampaignDeliveryStats,
  engagement?: LeadCampaignEngagementStats
): CampaignStatItem[] =>
  [
    { label: 'Failed', value: delivery?.failed ?? 0 },
    { label: 'Skipped', value: delivery?.skipped ?? 0 },
    { label: 'Bounced', value: engagement?.bounced ?? 0 },
    { label: 'Spam reports', value: engagement?.spam_reports ?? 0 },
    { label: 'Unsubscribed', value: engagement?.unsubscribed ?? 0 },
  ].filter((item) => Number(item.value) > 0);

const LEAD_TIMESTAMP_FORMATS = ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ', moment.ISO_8601];

/** Fallback display zone when a community has no timezone configured. */
export const LEAD_DEFAULT_TIMEZONE = 'America/New_York';

const LEAD_MERGE_TAG_PATTERN = /\{\{\s*[\w.]+\s*\}\}/g;

export type LeadTextSegment = { text: string; isMergeTag: boolean };

/** CSS class for display-only merge-tag chips (React and HTML wrap). */
export const LEAD_MERGE_TAG_CLASS = 'lead-merge-tag';

/**
 * Split display text into plain and {{merge_tag}} segments for chip rendering.
 * Display-only — never persist wrapped markup to drafts or the API.
 */
export const splitLeadMergeTags = (value?: string | null): LeadTextSegment[] => {
  const raw = value ?? '';
  const segments: LeadTextSegment[] = [];
  const matcher = new RegExp(LEAD_MERGE_TAG_PATTERN.source, 'g');
  let lastIndex = 0;
  let match = matcher.exec(raw);
  while (match) {
    if (match.index > lastIndex) {
      segments.push({ text: raw.slice(lastIndex, match.index), isMergeTag: false });
    }
    segments.push({ text: match[0], isMergeTag: true });
    lastIndex = match.index + match[0].length;
    match = matcher.exec(raw);
  }
  if (lastIndex < raw.length || segments.length === 0) {
    segments.push({ text: raw.slice(lastIndex), isMergeTag: false });
  }
  return segments;
};

/**
 * Wrap {{merge_tag}} tokens in HTML text nodes only.
 * Skips tag attributes and is idempotent for already-wrapped chips.
 */
export const wrapLeadMergeTagsInHtml = (html: string): string => {
  if (!html || !html.includes('{{')) return html;
  const tokens = html.split(/(<[^>]+>)/g);
  let mergeTagDepth = 0;
  return tokens
    .map((token) => {
      if (token.startsWith('<')) {
        if (/<code\b[^>]*\blead-merge-tag\b/i.test(token) && !/^<\//.test(token)) {
          mergeTagDepth += 1;
        } else if (/^<\/code>/i.test(token) && mergeTagDepth > 0) {
          mergeTagDepth -= 1;
        }
        return token;
      }
      if (mergeTagDepth > 0) return token;
      return splitLeadMergeTags(token)
        .map((segment) =>
          segment.isMergeTag
            ? `<code class="${LEAD_MERGE_TAG_CLASS}">${segment.text}</code>`
            : segment.text
        )
        .join('');
    })
    .join('');
};

/**
 * Parse a leads CRM timestamp into the display timezone.
 * The API stores naive UTC strings, so zone-less values are read as UTC;
 * values carrying an explicit offset keep their own instant.
 */
const parseLeadTimestamp = (value: string, timezone: string) => {
  const parsed = moment.utc(value, LEAD_TIMESTAMP_FORMATS, true);
  return parsed.isValid() ? parsed.tz(timezone) : null;
};

/**
 * Human-readable timestamp in the community timezone:
 * relative under 24h, yesterday, then a friendly date.
 */
export const formatLeadFriendlyTimestamp = (
  value?: string | null,
  timezone: string = LEAD_DEFAULT_TIMEZONE,
  now?: Date
): string => {
  const raw = (value ?? '').trim();
  if (!raw) return '—';
  const zone = timezone || LEAD_DEFAULT_TIMEZONE;
  const parsed = parseLeadTimestamp(raw, zone);
  if (!parsed) return '—';

  const current = (now ? moment(now) : moment()).tz(zone);
  const diffMs = current.diff(parsed);
  const diffMinutes = Math.max(0, current.diff(parsed, 'minutes'));
  const diffHours = Math.max(0, current.diff(parsed, 'hours'));

  if (diffMs >= 0 && diffMs < 60 * 1000) return 'Just now';
  if (diffMs >= 0 && diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffMs >= 0 && diffMs < 24 * 60 * 60 * 1000) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }
  if (parsed.isSame(current.clone().subtract(1, 'day'), 'day')) {
    return `Yesterday, ${parsed.format('h:mm A')}`;
  }
  if (parsed.isSame(current, 'year')) {
    return parsed.format('MMM D, h:mm A');
  }
  return parsed.format('MMM D, YYYY, h:mm A');
};

/** Full timestamp with zone abbreviation, for hover titles. */
export const formatLeadFullTimestamp = (
  value?: string | null,
  timezone: string = LEAD_DEFAULT_TIMEZONE
): string => {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  const parsed = parseLeadTimestamp(raw, timezone || LEAD_DEFAULT_TIMEZONE);
  return parsed ? parsed.format('MMM D, YYYY, h:mm A z') : '';
};

/** True when a terminal campaign only simulated sends (no real deliveries). */
export const isTestOnlyCampaign = (campaign?: LeadCampaignSchema): boolean => {
  if (!campaign || !isTerminalCampaignStatus(campaign.status)) return false;
  const stats = campaign.delivery_stats;
  return (stats?.sent ?? 0) === 0 && (stats?.simulated ?? 0) > 0;
};

/** Whether the campaign can be edited in the composer. */
export const canEditCampaign = (status?: string): boolean =>
  status === 'draft' || status === 'failed' || status === 'partial_failure';

/** Whether failed recipients can be retried via resend. */
export const canResendCampaign = (status?: string): boolean =>
  status === 'failed' || status === 'partial_failure';

/** Whether delete is allowed for this campaign. */
export const canDeleteCampaign = (campaign?: LeadCampaignSchema): boolean => {
  if (!campaign?.id || campaign.status === 'sending') return false;
  if (campaign.status === 'draft' || campaign.status === 'failed' || campaign.status === 'partial_failure') {
    return true;
  }
  return isTestOnlyCampaign(campaign);
};

/** User-facing confirm message when deleting a campaign. */
export const campaignDeleteConfirmMessage = (campaign?: LeadCampaignSchema): string => {
  const name = campaign?.name || 'Untitled';
  if (campaign?.status === 'draft') return `Delete draft "${name}"?`;
  if (isTestOnlyCampaign(campaign)) return `Delete test campaign "${name}"?`;
  return `Delete campaign "${name}"? This cannot be undone.`;
};

/** User-facing confirm message when resending failed campaign recipients. */
export const campaignResendConfirmMessage = (campaign?: LeadCampaignSchema): string => {
  const name = campaign?.name || 'Untitled';
  const failedCount = campaign?.delivery_stats?.failed ?? 0;
  const skippedCount = campaign?.delivery_stats?.skipped ?? 0;
  const retryCount = failedCount + skippedCount;
  if (retryCount > 0) {
    return `Retry ${retryCount} failed/skipped recipient${retryCount === 1 ? '' : 's'} for "${name}"?`;
  }
  return `Retry failed recipients for "${name}"?`;
};

/** Promise-based delay for polling loops (prefer over window.setTimeout in components). */
export const waitMs = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Returns true when campaign status is no longer in progress. */
export const isTerminalCampaignStatus = (status?: string): boolean =>
  status === 'completed' || status === 'partial_failure' || status === 'failed';

/** Human-readable summary of who will receive a mass email send. */
export const describeNewsletterRecipients = (input: {
  total: number;
  activeGroupName?: string | null;
  sampleLeads?: Array<{ email?: string; business_name?: string }>;
}): string => {
  if (input.activeGroupName) {
    return `Recipients: ${input.total} lead${input.total === 1 ? '' : 's'} in saved group "${input.activeGroupName}".`;
  }
  if (input.total <= 0) {
    return 'Recipients: no leads match the current filters.';
  }

  const previewNames = (input.sampleLeads ?? [])
    .slice(0, 3)
    .map((lead) => lead.email || lead.business_name)
    .filter(Boolean);

  const preview =
    previewNames.length > 0
      ? ` Includes ${previewNames.join(', ')}${input.total > previewNames.length ? ` and ${input.total - previewNames.length} more` : ''}.`
      : '';

  return `Recipients: ${input.total} lead${input.total === 1 ? '' : 's'} matching current filters.${preview}`;
};

/** Extract a user-facing API error message from axios/fetch failures. */
export const formatLeadApiErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string; message?: string }; status?: number } })
      .response;
    const apiMessage = response?.data?.error || response?.data?.message;
    if (apiMessage) {
      return response?.status ? `${apiMessage} (${response.status})` : apiMessage;
    }
    if (response?.status) {
      return `${fallback} (HTTP ${response.status})`;
    }
  }
  if (error instanceof Error && error.message) {
    return `${fallback}: ${error.message}`;
  }
  return fallback;
};

export type LeadColorToken = {
  primary: string;
  background: string;
};

/** Returns true when the signed-in user may open /leads. */
export const isLeadsAdminEmail = (email?: string | null): boolean => {
  if (!email) return false;
  return DEFAULT_LEADS_ADMIN_EMAILS.includes(email.trim().toLowerCase());
};

export const LEAD_STAGE_OPTIONS = [
  { value: 'awareness', label: 'Awareness' },
  { value: 'lead_generation', label: 'Lead Generation' },
  { value: 'qualification', label: 'Qualification' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'activation', label: 'Activation' },
  { value: 'retention_growth', label: 'Retention & Growth' },
] as const;

export const LEAD_STATUS_OPTIONS = [
  { value: 'new_lead', label: 'New Lead' },
  { value: 'qualified_lead', label: 'Qualified Lead' },
  { value: 'hot_lead', label: 'Hot Lead' },
  { value: 'customer', label: 'Customer' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'service_ended', label: 'Service Ended' },
  { value: 'passed', label: 'Passed' },
] as const;

export const LEAD_CATEGORY_OPTIONS = [
  { value: 'food_entrepreneur', label: 'Food Entrepreneur' },
  { value: 'resource_provider', label: 'Resource Provider' },
] as const;

export const LEAD_RESOURCE_TYPE_OPTIONS = [
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'fresh_food', label: 'Fresh Food' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'training', label: 'Training' },
  { value: 'funding', label: 'Funding' },
  { value: 'expert', label: 'Expert' },
  { value: 'farm', label: 'Farm' },
] as const;

export const LEAD_STAGE_COLORS: Record<string, LeadColorToken> = {
  awareness: { primary: '#7C3AED', background: '#F3E8FF' },
  lead_generation: { primary: '#2563EB', background: '#EFF6FF' },
  qualification: { primary: '#16A34A', background: '#ECFDF3' },
  onboarding: { primary: '#CA8A04', background: '#FEFCE8' },
  activation: { primary: '#EA580C', background: '#FFF7ED' },
  retention_growth: { primary: '#DC2626', background: '#FEF2F2' },
};

export const LEAD_STATUS_COLORS: Record<string, LeadColorToken> = {
  new_lead: { primary: '#5DB6CE', background: '#EFF8FA' },
  qualified_lead: { primary: '#475569', background: '#F1F5F9' },
  hot_lead: { primary: '#B45309', background: '#FFF4E5' },
  customer: { primary: '#0F973D', background: '#E8F5EC' },
  on_hold: { primary: '#737373', background: '#F5F5F5' },
  service_ended: { primary: '#9CA3AF', background: '#F3F4F6' },
  passed: { primary: '#DC2626', background: '#FEF2F2' },
};

export const labelForLeadStage = (value?: string) =>
  LEAD_STAGE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '-';

export const labelForLeadStatus = (value?: string) =>
  LEAD_STATUS_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '-';

export const labelForLeadCategory = (value?: string) =>
  LEAD_CATEGORY_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '-';

/** Extract the domain portion from a lead email. */
export const emailDomainFromLead = (email?: string | null): string => {
  const normalized = (email ?? '').trim().toLowerCase();
  if (!normalized.includes('@')) return '';
  return normalized.split('@')[1] ?? '';
};

/** Build a stable slug key from a business name. */
export const normalizeBusinessKey = (businessName?: string | null): string => {
  const raw = (businessName ?? '').trim().toLowerCase();
  return raw.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
};

/** Resolve color tokens for a lead stage value. */
export const getLeadStageColor = (value?: string): LeadColorToken =>
  LEAD_STAGE_COLORS[value ?? ''] ?? { primary: '#737373', background: '#F5F5F5' };

/** Resolve color tokens for a lead status value. */
export const getLeadStatusColor = (value?: string): LeadColorToken =>
  LEAD_STATUS_COLORS[value ?? ''] ?? { primary: '#737373', background: '#F5F5F5' };

/** Format city, county, state, and zip as a single aligned location string. */
export const formatLeadLocation = (lead: {
  city?: string;
  county?: string;
  state?: string;
  business_zip_code?: string;
}): string => {
  const cityState = [lead.city, lead.state].filter(Boolean).join(', ');
  const countyPart = lead.county ? `${lead.county} County` : '';
  const parts = [cityState, countyPart, lead.business_zip_code].filter(Boolean);
  return parts.length ? parts.join(' · ') : '-';
};

/** Map draft-contact filter UI state to API pending_email param. */
export const pendingEmailParamForDraftFilter = (
  filter: DraftLeadFilter
): 'true' | 'false' | undefined => {
  if (filter === 'only') return 'true';
  if (filter === 'exclude') return 'false';
  return undefined;
};

/** Map test-lead filter UI state to API query params. */
export const testLeadParamsForFilter = (
  filter: TestLeadFilter
): { test_leads?: TestLeadFilter; include_test?: boolean } => {
  if (filter === 'include') return { test_leads: 'include', include_test: true };
  if (filter === 'only') return { test_leads: 'only' };
  return { test_leads: 'exclude' };
};

export type LeadListFilterParamsInput = {
  debouncedSearch?: string;
  categoryFilter?: string;
  filterStage?: string;
  filterStatus?: string;
  filterState?: string;
  filterSignupSource?: string;
  filterResourceType?: string;
  draftLeadFilter?: DraftLeadFilter;
  testLeadFilter?: TestLeadFilter;
  activeGroupId?: string | null;
  page?: number;
  pageSize?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
};

/** Build shared list/stats query params from current CRM filter UI state. */
export const buildLeadListFilterParams = (
  input: LeadListFilterParamsInput
): Record<string, string | number | boolean | undefined> => {
  const pagination = {
    page: input.page,
    limit: input.pageSize,
    sort: input.sort,
    dir: input.dir,
  };

  // When viewing a saved group, let the backend resolve filter/search/membership
  // from the group document to avoid double-applying criteria in UI + group_id.
  if (input.activeGroupId) {
    return {
      group_id: input.activeGroupId,
      ...pagination,
    };
  }

  const pendingEmail = pendingEmailParamForDraftFilter(
    input.draftLeadFilter ?? DEFAULT_DRAFT_LEAD_FILTER
  );
  const testParams = testLeadParamsForFilter(input.testLeadFilter ?? DEFAULT_TEST_LEAD_FILTER);

  return {
    q: input.debouncedSearch || undefined,
    lead_category: input.categoryFilter || undefined,
    lead_stage: input.filterStage || undefined,
    lead_status: input.filterStatus || undefined,
    state: input.filterState || undefined,
    signup_source: input.filterSignupSource || undefined,
    resource_type: input.filterResourceType || undefined,
    pending_email: pendingEmail,
    ...testParams,
    ...pagination,
  };
};

/** Build Mongo-compatible filter snapshot for saving a lead group. */
export const buildLeadGroupFilterSnapshot = (input: {
  categoryFilter?: string;
  filterStage?: string;
  filterStatus?: string;
  filterState?: string;
  draftLeadFilter?: DraftLeadFilter;
  testLeadFilter?: TestLeadFilter;
}): Record<string, unknown> => {
  const snapshot: Record<string, unknown> = {};
  if (input.categoryFilter) snapshot.lead_category = input.categoryFilter;
  if (input.filterStage) snapshot.lead_stage = input.filterStage;
  if (input.filterStatus) snapshot.lead_status = input.filterStatus;
  if (input.filterState) snapshot.state = input.filterState;

  const pendingEmail = pendingEmailParamForDraftFilter(
    input.draftLeadFilter ?? DEFAULT_DRAFT_LEAD_FILTER
  );
  if (pendingEmail === 'true') snapshot.pending_email = true;
  // Omit default "exclude drafts" - it matches almost all leads and is not a meaningful group criterion.

  const testFilter = input.testLeadFilter ?? DEFAULT_TEST_LEAD_FILTER;
  if (testFilter === 'only') snapshot.is_test_lead = true;
  // Omit default "exclude test leads" for the same reason.

  return snapshot;
};

/** Restore UI filter controls from a saved group document. */
export const filtersFromLeadGroup = (
  group: {
    filter?: Record<string, unknown>;
    search_q?: string;
  }
): Partial<LeadFilterState> => {
  const filter = group.filter ?? {};
  let draftLeadFilter: DraftLeadFilter = DEFAULT_DRAFT_LEAD_FILTER;
  if (filter.pending_email === true) {
    draftLeadFilter = 'only';
  } else if (
    filter.pending_email &&
    typeof filter.pending_email === 'object' &&
    (filter.pending_email as { $ne?: boolean }).$ne === true
  ) {
    draftLeadFilter = 'exclude';
  }

  let testLeadFilter: TestLeadFilter = DEFAULT_TEST_LEAD_FILTER;
  if (filter.is_test_lead === true) {
    testLeadFilter = 'only';
  } else if (
    filter.is_test_lead &&
    typeof filter.is_test_lead === 'object' &&
    (filter.is_test_lead as { $ne?: boolean }).$ne === true
  ) {
    testLeadFilter = 'exclude';
  }

  return {
    searchInput: group.search_q ?? '',
    filterStage: typeof filter.lead_stage === 'string' ? filter.lead_stage : '',
    filterStatus: typeof filter.lead_status === 'string' ? filter.lead_status : '',
    filterState: typeof filter.state === 'string' ? filter.state : '',
    draftLeadFilter,
    testLeadFilter,
    activeGroupId: null,
  };
};

/** Returns true when any non-default CRM filter is active. */
export const hasActiveLeadFilters = (
  filters: LeadFilterState,
  categoryFilter?: string
): boolean =>
  Boolean(
    filters.searchInput.trim() ||
      filters.filterStage ||
      filters.filterStatus ||
      filters.filterState ||
      filters.filterSignupSource ||
      filters.filterResourceType ||
      filters.draftLeadFilter !== DEFAULT_DRAFT_LEAD_FILTER ||
      filters.testLeadFilter !== DEFAULT_TEST_LEAD_FILTER ||
      filters.activeGroupId ||
      categoryFilter
  );

/** Title-case a city or county label for table display. */
export const formatLeadCityOrCounty = (value?: string | null): string => {
  const raw = (value ?? '').trim();
  if (!raw) return '-';
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

/** Normalize state to a two-letter abbreviation for table display. */
export const formatLeadStateAbbrev = (value?: string | null): string => {
  const raw = (value ?? '').trim();
  if (!raw) return '-';
  const upper = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  const byValue = US_STATE_OPTIONS.find((option) => option.value === upper);
  if (byValue) return byValue.value;
  const byLabel = US_STATE_OPTIONS.find(
    (option) => option.label.toLowerCase() === raw.toLowerCase()
  );
  if (byLabel) return byLabel.value;
  return upper.slice(0, 2);
};

/** Saved groups pinned to the CRM tab bar, in stable name order. */
export const pinnedLeadGroupsFromList = (
  groups: Array<{ id?: string; name?: string; pinned_to_tab_bar?: boolean }>
): Array<{ id?: string; name?: string; pinned_to_tab_bar?: boolean }> =>
  groups
    .filter((group) => group.pinned_to_tab_bar && group.id)
    .sort((left, right) => (left.name ?? '').localeCompare(right.name ?? ''));

export type LeadGroupByKey = 'none' | 'lead_stage' | 'lead_status' | 'lead_category' | 'state' | 'city';

/** Build grouped table sections from a flat lead list. */
export const groupLeadsByField = <T extends Record<string, unknown>>(
  leads: T[],
  field: LeadGroupByKey,
  labelForValue: (value?: string) => string = (value) => value ?? '-'
): Array<{ key: string; label: string; items: T[] }> => {
  if (field === 'none') {
    return [{ key: 'all', label: 'All leads', items: leads }];
  }

  const buckets = new Map<string, T[]>();
  leads.forEach((lead) => {
    const raw = String(lead[field] ?? 'unknown');
    const existing = buckets.get(raw) ?? [];
    existing.push(lead);
    buckets.set(raw, existing);
  });

  return Array.from(buckets.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, items]) => ({
      key,
      label: labelForValue(key === 'unknown' ? undefined : key),
      items,
    }));
};
